import React, { useEffect, useMemo, useState } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  CartesianGrid,
  Legend,
  ReferenceLine,
  BarChart,
  Bar
} from 'recharts';
import { DatePicker } from 'antd';
import { 
  ChartBar, 
  ChartLine, 
  CalendarRange, 
  MoreVertical, 
  TrendingUp, 
  TrendingDown,
  MapPin
} from 'lucide-react';
import { FilterParams } from '../leadsGrid/leads.types';
import { distributionChartService } from '../../../services/distributionChartService';
import './DistributionChart.css';
import moment, { Moment } from 'moment';
import LocationMapChart from './LocationMapChart';

interface Lead {
  _id: string;
  createdAt: string;
  [key: string]: any;
}

interface DistributionChartProps {
  field: string;
  isOpen?: boolean;
  onClose?: () => void;
  filterParams: FilterParams;
}

interface ChartDataPoint {
  date: string;
  count: number;
  _id: string;
  type?: string;
  displayName?: string;
}

interface Insights {
  total: number;
  average: number;
  max: number;
  trend: 'up' | 'down';
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const { RangePicker } = DatePicker;

const DistributionChart: React.FC<DistributionChartProps> = ({ 
  field, 
  filterParams,
  isOpen = true,
  onClose = () => {}
}): JSX.Element => {
  

  // State
  const [dateRange, setDateRange] = useState<{start?: Date, end?: Date}>({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Check if field is a location field
  const isLocationField = useMemo(() => {
    return field.includes('location') || field.includes('address') || field.includes('coords');
  }, [field]);
  
  // For status and label, we only use bar chart
  // For location, we'll use a map
  const [chartType, setChartType] = useState<'line' | 'bar'>(() => 
    field === 'createdAt' ? 'line' : 'bar'
  );
  
  // Only allow line/bar toggle for temporal data
  const canToggleChartType = field === 'createdAt';

  // Initialize with default 7-day range
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return {
      startDate: start,
      endDate: end
    };
  });

  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data processing
const processedData = useMemo(() => {
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return [];
  }

  if (field === 'createdAt') {
    const processed = chartData.map((d: ChartDataPoint) => ({
      ...d,
      date: new Date(d.date).toLocaleDateString()
    }));
    
    return processed;
  } else if (field === 'assignedTo') {
    // 🎯 SPECIAL HANDLING FOR ASSIGNED TO
    const processed = chartData.map((d: any) => ({
      ...d,
      _id: d.assignedTo,                    // Map assignedTo to _id
      displayName: d.assignedUserName,      // Map assignedUserName to displayName
      count: Number(d.count)
    }));

    return processed;
  } else {
    const processed = chartData.map((d: ChartDataPoint) => ({
      ...d,
      displayName: d.type || d.displayName || d._id,
      count: Number(d.count)
    }));

    return processed;
  }
}, [chartData, field]);

  // Insights calculation
  const insights = useMemo(() => {
  
    if (field !== 'createdAt' || !processedData.length) {
    
      return null;
    }

    const counts = processedData.map(d => d.count);
    const total = counts.reduce((sum: number, count: number) => sum + count, 0);
    const average = Math.round(total / counts.length);
    const max = Math.max(...counts);
    const trend = counts[counts.length - 1] > counts[0] ? 'up' : 'down';

    const insightData = { total, average, max, trend };
   
    return insightData;
  }, [processedData, field]);

  useEffect(() => {
    // Skip data fetching for location fields - handled by LocationMapChart
    if (isLocationField) return;
    
    const fetchData = async () => {
     
  
      setIsLoading(true);
      setError(null);
  
      try {
        // Properly structure the date parameters
        const finalFilterParams = {
          ...filterParams, // This now includes the list ID from LeadsGrid
          date: selectedDateRange.startDate && selectedDateRange.endDate ? {
            startedAt: selectedDateRange.startDate.toISOString(),
            endedAt: selectedDateRange.endDate.toISOString()
          } : undefined
        };
  
      
  
        const response = await distributionChartService.fetchDistributionData(
          finalFilterParams, 
          field
        );
        
        if (Array.isArray(response.data)) {
         
          setChartData(response.data);
        } else {
        
          setError('Invalid data format received from server');
          setChartData([]);
        }
      } catch (error) {
       
        setError(error instanceof Error ? error.message : 'Failed to fetch data');
        setChartData([]);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [selectedDateRange, filterParams, field, isLocationField]);

  const renderChart = () => {
  
    
    // If this is a location field, render the map component
    if (isLocationField) {
      return (
        <LocationMapChart
          field={field}
          filterParams={filterParams}
          onClose={onClose}
        />
      );
    }
    
    if (isLoading) {
      return <div className="loading-state">Loading chart data...</div>;
    }

    if (error) {
      return <div className="error-state">Error: {error}</div>;
    }

    if (!processedData.length) {
      return <div className="no-data-state">No data available</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={450}>
        {chartType === 'line' ? (
          <LineChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={field === 'createdAt' ? 'date' : 'displayName'}
              tickFormatter={field === 'createdAt' ? 
                (tick: string) => new Date(tick).toLocaleDateString() : 
                undefined
              }
            />
            <YAxis />
            <Tooltip />
            {field === 'createdAt' && insights && (
              <ReferenceLine 
                y={insights.average} 
                stroke="#3b82f6" 
                strokeDasharray="3 3" 
                label="Average"
              />
            )}
            <Line
              type="monotone"
              dataKey="count"
              stroke="#3b82f6"
              strokeWidth={2}
            />
          </LineChart>
        ) : (
          <BarChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={field === 'createdAt' ? 'date' : 'displayName'} />
            <YAxis />
            <Tooltip />
            {field === 'createdAt' && insights && (
              <ReferenceLine 
                y={insights.average} 
                stroke="#3b82f6" 
                strokeDasharray="3 3" 
                label="Average"
              />
            )}
            <Bar dataKey="count" fill="#3b82f6" />
          </BarChart>
        )}
      </ResponsiveContainer>
    );
  };

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
   
    
    if (!dates || !dates[0] || !dates[1]) {
      // If dates are cleared, reset to default 7-day range
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      
      setSelectedDateRange({
        startDate: start,
        endDate: end
      });
      return;
    }
    
    setSelectedDateRange({
      startDate: dates[0].toDate(),
      endDate: dates[1].toDate()
    });
  };

  // For location fields, just render the LocationMapChart component
  if (isLocationField) {
    return (
      <LocationMapChart
        field={field}
        filterParams={filterParams}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="distribution-chart-wrapper">
      <div className="distribution-chart-header">
        <div className="distribution-chart-title">
          {field} Distribution
        </div>
        
        {field === 'createdAt' && insights && (
          <div className="distribution-chart-header-metrics">
            <div className="distribution-chart-header-metrics-item">
              <span className="distribution-chart-header-metrics-label">Total:</span>
              <span className="distribution-chart-header-metrics-value">{insights.total}</span>
            </div>
            <div className="distribution-chart-header-metrics-item">
              <span className="distribution-chart-header-metrics-label">Avg:</span>
              <span className="distribution-chart-header-metrics-value">{insights.average}</span>
            </div>
            <div className="distribution-chart-header-metrics-item">
              <span className="distribution-chart-header-metrics-label">Peak:</span>
              <span className="distribution-chart-header-metrics-value">{insights.max}</span>
            </div>
            <div className={`distribution-chart-header-metrics-item distribution-chart-trend-${insights.trend}`}>
              <span className="distribution-chart-header-metrics-label">Trend:</span>
              <span className="distribution-chart-header-metrics-value">
                {insights.trend === 'up' ? 'Rising' : 'Falling'}
              </span>
            </div>
          </div>
        )}

        {/* Always show date range for context */}
        <div className="distribution-chart-date-range" id="chart-date-range-container">
  <RangePicker
    onChange={handleDateRangeChange}
    value={[
      selectedDateRange.startDate ? moment(selectedDateRange.startDate) : null,
      selectedDateRange.endDate ? moment(selectedDateRange.endDate) : null
    ]}
    placeholder={['Start Date', 'End Date']}
    allowClear={false}
    getPopupContainer={() => document.getElementById('chart-date-range-container') || document.body}
    style={{ minWidth: '300px' }}
    dropdownClassName="distribution-chart-date-dropdown"
    ranges={{
      'Today': [moment(), moment()],
      'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
      'Last 7 Days': [moment().subtract(6, 'days'), moment()],
      'Last 14 Days': [moment().subtract(13, 'days'), moment()],
      'Last 30 Days': [moment().subtract(29, 'days'), moment()],
      'This Month': [moment().startOf('month'), moment()],
      'Last Month': [
        moment().subtract(1, 'months').startOf('month'),
        moment().subtract(1, 'months').endOf('month')
      ],
      'Last Quarter': [
        moment().subtract(3, 'months').startOf('month'),
        moment()
      ]
    }}
  />
</div>
    
        <div className="distribution-chart-actions">
          {/* Only show chart type toggle for temporal data */}
          {canToggleChartType && (
            <button 
              className="distribution-chart-action-btn"
              onClick={() => {
               
                setChartType(chartType === 'line' ? 'bar' : 'line');
              }}
              title={`Switch to ${chartType === 'line' ? 'Bar' : 'Line'} Chart`}
            >
              {chartType === 'line' ? 'Bar' : 'Line'}
            </button>
          )}
          <button 
            className="distribution-chart-action-btn"
            title="More Options"
          >
            ...
          </button>
        </div>
      </div>
  
      <div className="distribution-chart-content">
        <div className="distribution-chart-visualizations">
          {renderChart()}
        </div>
      </div>
    </div>
  );
}

export default DistributionChart;
